import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Button } from '../../../../Controls/Button';
import { NothingFound } from '../../../../Elements/Images';
import { Loader } from '../../../../Elements/Loader';
import { DEFAULT_DROPDOWN_WIDTH, UNGROUPED_KEY } from '../../constants';
import { groupOptions, hasGroups, isEqualOptions } from '../../helpers';
import { Search, AUTOFOCUS_WITHOUT_SCROLL } from '../Search';
import { OptionsList } from './OptionsList';
import { DropdownBody, FooterWrapper, GroupHeaderWrapper, HeaderWrapper, OptionsWrapper, DropdownWrapper } from './styled';
export const Dropdown = ({ onChange, selected, isMulti, loading, isClearable = true, getOptionKey, serverSideFiltering, filter, setFilter, filteredOptions, total, noMatchesText = 'No matches found', clearSelectionText = 'Clear selection', getFilteredText = (filtered, totalItems) => `${filtered} of ${totalItems}`, getSelectedText = (totalSelected) => `${totalSelected} selected`, getTotalText = (totalItems) => `${totalItems} total`, optionSelected, close, formatOptionLabel, autofocus = AUTOFOCUS_WITHOUT_SCROLL, dropdownWidth = DEFAULT_DROPDOWN_WIDTH, }) => {
    const theme = useTheme();
    // Keyboard navigation state
    const [focusedIndex, setFocusedIndex] = useState(-1);
    // eslint-disable-next-line no-restricted-syntax
    const handleClick = useCallback((option) => () => {
        if (isMulti) {
            const copy = [...(selected ?? [])];
            const index = copy.findIndex((item) => (optionSelected ? optionSelected(item, option) : isEqualOptions(item, option)));
            if (index !== -1) {
                if (isClearable || copy.length > 1) {
                    copy.splice(index, 1);
                }
            }
            else {
                copy.push(option);
            }
            onChange(copy);
        }
        else if (isClearable) {
            if (optionSelected) {
                onChange(selected && optionSelected(selected, option) ? null : option);
            }
            else {
                onChange(selected && isEqualOptions(selected, option) ? null : option);
            }
            close();
        }
        else {
            onChange(option);
            close();
        }
    }, [close, isClearable, isMulti, onChange, optionSelected, selected]);
    const handleClear = useCallbackRef(() => {
        if (!isClearable) {
            return;
        }
        if (isMulti) {
            onChange([]);
        }
        else {
            onChange(null);
        }
    });
    const handleSearchChange = useCallbackRef((e) => {
        setFilter(e.target.value);
    });
    const handleSearchClear = useCallbackRef(() => {
        setFilter('');
    });
    // Keyboard navigation handler
    const handleKeyDown = useCallbackRef((e) => {
        const optionsToNavigate = filteredOptions;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex((prev) => (prev < optionsToNavigate.length - 1 ? prev + 1 : 0));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex((prev) => (prev > 0 ? prev - 1 : optionsToNavigate.length - 1));
                break;
            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < optionsToNavigate.length) {
                    const option = optionsToNavigate[focusedIndex];
                    if (option) {
                        handleClick(option)();
                    }
                }
                break;
            case 'Escape':
                e.preventDefault();
                close();
                break;
            case 'Tab':
                // Allow default Tab behavior but close the dropdown
                close();
                break;
            default:
                break;
        }
    });
    // Reset focused index when filter changes
    useEffect(() => {
        setFocusedIndex(filteredOptions.length > 0 ? 0 : -1);
    }, [filter, filteredOptions.length]);
    const hasValue = useMemo(() => Boolean(isMulti ? selected.length : selected), [isMulti, selected]);
    const selectedCount = useMemo(() => (Array.isArray(selected) ? selected.length : 1), [selected]);
    const stateDescription = useMemo(() => {
        if (filteredOptions.length === total && !hasValue) {
            return getTotalText(total);
        }
        if (hasValue) {
            return getSelectedText(selectedCount);
        }
        return getFilteredText(filteredOptions.length, total);
    }, [filteredOptions.length, getFilteredText, getSelectedText, getTotalText, hasValue, selectedCount, total]);
    const withGroups = useMemo(() => hasGroups(filteredOptions), [filteredOptions]);
    const groupedOptions = useMemo(() => (withGroups ? groupOptions(filteredOptions) : null), [withGroups, filteredOptions]);
    return (_jsxs(DropdownWrapper, { onKeyDown: handleKeyDown, tabIndex: -1, role: 'listbox', "aria-multiselectable": isMulti, children: [(!!serverSideFiltering || (!loading && total > 6)) && (_jsx(HeaderWrapper, { children: _jsx(Search, { value: filter, onChange: handleSearchChange, onClear: handleSearchClear, autofocus: autofocus }) })), !loading && (_jsxs(_Fragment, { children: [filteredOptions.length === 0 && (_jsxs(DropdownBody, { "data-testid": 'no-matches', children: [_jsx(NothingFound, { size: 90 }), noMatchesText] })), filteredOptions.length > 0 && (_jsxs(_Fragment, { children: [_jsx(OptionsWrapper, { children: groupedOptions ? (groupedOptions.map((group, groupIndex) => {
                                    // Calculate the starting index for this group
                                    const startIndex = groupedOptions.slice(0, groupIndex).reduce((acc, g) => acc + g.options.length, 0);
                                    return (_jsxs(Fragment, { children: [group.groupName !== UNGROUPED_KEY && _jsx(GroupHeaderWrapper, { children: group.groupName }), _jsx(OptionsList, { options: group.options, groupName: group.groupName, isMulti: isMulti, selected: selected, focusedIndex: focusedIndex - startIndex, getOptionKey: getOptionKey, optionSelected: optionSelected, formatOptionLabel: formatOptionLabel, handleClick: handleClick })] }, group.groupName));
                                })) : (_jsx(OptionsList, { options: filteredOptions, isMulti: isMulti, selected: selected, focusedIndex: focusedIndex, getOptionKey: getOptionKey, optionSelected: optionSelected, formatOptionLabel: formatOptionLabel, handleClick: handleClick })) }), _jsxs(FooterWrapper, { width: dropdownWidth, "data-testid": 'filter-footer', children: [_jsx("span", { children: isClearable && hasValue && (_jsx(Button, { ghost: true, dataTestId: 'clear-selection', onClick: handleClear, children: clearSelectionText })) }), _jsx("span", { "data-testid": 'footer-selected-amount', children: stateDescription })] })] }))] })), loading && (_jsx(DropdownBody, { children: _jsx(Loader, { fill: theme.colors.grey100_1 }) }))] }));
};
export const DropdownWithFilterState = (props) => {
    const { options, formatOptionLabel } = props;
    const [filter, setFilter] = useState('');
    const filteredOptions = useMemo(() => options
        .filter((option) => {
        if (filter.length === 0)
            return true;
        const lowerFilter = filter.toLowerCase();
        const formattedLabel = formatOptionLabel?.(option);
        const label = typeof formattedLabel === 'string' ? formattedLabel : option.label;
        const matchesLabel = label.toLowerCase().includes(lowerFilter);
        const matchesGroup = option.group?.toLowerCase().includes(lowerFilter) || false;
        return matchesLabel || matchesGroup;
    })
        .slice(0, 100), [options, filter, formatOptionLabel]);
    const totalCount = useMemo(() => options.length, [options.length]);
    const dropdownProps = useMemo(() => ({
        ...props,
        filter,
        setFilter,
        filteredOptions,
        total: totalCount,
    }), [props, filter, setFilter, filteredOptions, totalCount]);
    return _jsx(Dropdown, { ...dropdownProps });
};
